import React, { useEffect, useMemo, useState } from 'react';
import './SharePickerModal.css';

// SP-side Share picker for a report attachment on a Service Request
// (spec §2 wireframe 2). Offers exactly two audience options — Requester
// plus the counterparty inferred from the uploader's vendorType (COMPANY
// → Inspector; INDIVIDUAL / unknown → Company). The uploader's own
// audience is deliberately absent; the server strips it silently on
// receipt so hiding it here just keeps the checkbox list matching the
// wireframe.
//
// Pure presentation + local checkbox state; parent (JobDetailPage) owns
// the PATCH .../share network call and the SR refresh after success.
const SharePickerModal = ({
  visible,
  attachment,
  uploaderVendorType,       // 'COMPANY' | 'INDIVIDUAL' | null
  // Distinguishes an INDIVIDUAL freelancer (null → Requester only) from an
  // on-staff inspector (parent set → Requester + Company). Both types are
  // INDIVIDUAL vendorType.
  uploaderParentCompanyUserId,
  requesterName,
  counterpartyName,
  submitting = false,
  error = null,
  onClose,
  onSubmit,
}) => {
  const options = useMemo(() => {
    // Requester is always an option — Requester is never the uploader.
    const requesterOpt = {
      audience: 'REQUESTER',
      title: 'Requester',
      subtitle: requesterName
        ? `${requesterName} — visible on their transaction Timeline`
        : 'Visible on their transaction Timeline',
    };
    // Per spec:
    //   Individual freelancer SP → only Requester
    //   Company SP                → Requester + Inspector
    //   On-staff Inspector SP     → Requester + Company
    if (uploaderVendorType === 'COMPANY') {
      return [
        requesterOpt,
        {
          audience: 'INSPECTOR',
          title: 'Inspector',
          subtitle: counterpartyName
            ? `${counterpartyName} — visible in this job's Docs tab`
            : "Visible in this job's Docs tab",
        },
      ];
    }
    if (uploaderVendorType === 'INDIVIDUAL' && uploaderParentCompanyUserId) {
      return [
        requesterOpt,
        {
          audience: 'COMPANY',
          title: 'Company',
          subtitle: counterpartyName
            ? `${counterpartyName} — visible in this job's Docs tab`
            : "Visible in this job's Docs tab",
        },
      ];
    }
    if (uploaderVendorType === 'INDIVIDUAL') {
      // Freelancer — no counterparty option.
      return [requesterOpt];
    }
    // vendorType not hydrated yet — fall back to Requester + Company; server
    // strips the uploader's own audience anyway.
    return [
      requesterOpt,
      {
        audience: 'COMPANY',
        title: 'Company',
        subtitle: counterpartyName
          ? `${counterpartyName} — visible in this job's Docs tab`
          : "Visible in this job's Docs tab",
      },
    ];
  }, [uploaderVendorType, uploaderParentCompanyUserId, requesterName, counterpartyName]);

  const alreadyShared = useMemo(() => {
    const raw = Array.isArray(attachment?.sharedWith) ? attachment.sharedWith : [];
    return new Set(raw);
  }, [attachment]);

  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    if (!visible) return;
    // Pre-check anything already granted so the pill state doesn't
    // visually regress if the SP just wants to add one more audience.
    const seed = new Set();
    options.forEach(o => { if (alreadyShared.has(o.audience)) seed.add(o.audience); });
    setSelected(seed);
  }, [visible, options, alreadyShared]);

  if (!visible || !attachment) return null;

  const toggle = (audience) => {
    if (submitting) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(audience)) next.delete(audience);
      else next.add(audience);
      return next;
    });
  };

  // Only enable Share when at least one net-new audience is selected —
  // re-sending an already-granted set would be a wasted round-trip.
  const newlySelected = Array.from(selected).filter(a => !alreadyShared.has(a));
  const canSubmit = newlySelected.length > 0 && !submitting;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    await onSubmit(Array.from(selected));
  };

  return (
    <div className="jp-share-overlay" onClick={submitting ? undefined : onClose}>
      <div className="jp-share-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="jp-share-title">Share document</h3>
        <p className="jp-share-subtitle" title={attachment.fileName}>
          {attachment.fileName}
        </p>

        <div className="jp-share-options">
          {options.map(opt => {
            const isChecked = selected.has(opt.audience);
            const already = alreadyShared.has(opt.audience);
            return (
              <button
                type="button"
                key={opt.audience}
                className={`jp-share-option${isChecked ? ' is-checked' : ''}`}
                onClick={() => toggle(opt.audience)}
                disabled={submitting}
              >
                <span className={`jp-share-checkbox${isChecked ? ' is-checked' : ''}`}>
                  {isChecked ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : null}
                </span>
                <span className="jp-share-option-text">
                  <span className="jp-share-option-title">{opt.title}</span>
                  <span className="jp-share-option-subtitle">
                    {already ? 'Already shared · ' : ''}{opt.subtitle}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {error ? <p className="jp-share-error">{error}</p> : null}

        <div className="jp-share-actions">
          <button
            type="button"
            className="jp-share-btn jp-share-btn-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="jp-share-btn jp-share-btn-primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? 'Sharing…' : 'Share'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SharePickerModal;
